import React, { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { IconButton, Sheet, toast } from "ui";
import {
  CommandSquareIcon,
  ExclamationIcon,
  PencilIcon,
  ShieldExclamationIcon,
} from "icons/outline";
import Layout from "../../../components/Layout";
import { axiosApiInstance } from "../../api/axios";
import { RiskRuleCard } from "../../../features/risk/components/RiskRuleCard";
import { useGlobalSearchQuery } from "../../../components/GlobalSearch";
import Loading from "../../../components/Loading";
import { Filters } from "../../../components/newFilters";
import { Pagination } from "../../../components/Pagination";
import { usePaginationParams } from "../../../hooks/usePaginationParams";
import { RiskRule } from "../../../features/risk/types";
import NoResults from "../../../components/NoResults/NoResults";
import { EditRuleDrawer } from "../../../features/risk/components/EditRuleDrawer";
import AlertModal from "../../../components/AlertModal";
import { useMoneyHashQuery } from "../../../hooks/useMoneyHashQuery";
import { useRestoreScrollOnFetching } from "../../../hooks/useRestoreScrollOnFetching";
import { useMutation } from "@tanstack/react-query";
import { parseErrorsToArray } from "../../../utils/parseErrors";
import { ErrorPage } from "../../../components/ErrorPage";
import { RiskNavigationTabs } from "../../../components/NavigationTabs/RiskNavigationTabs";
import { PageHeader } from "../../../components/PageHeading";
import { AddNewRuleDrawer } from "../../../features/risk/components/AddNewRuleDrawer";

export default function RiskRulesPage() {
  const router = useRouter();

  const searchQuery = useGlobalSearchQuery();
  const [paginationParams] = usePaginationParams();

  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [showEditRule, setShowEditRule] = useState(false);
  const [ruleIdToDelete, setRuleIdToDelete] = useState<string | null>(null);

  const {
    data: rules,
    count,
    listMeta,
    refetch,
    isLoading,
    isFetching,
    isError,
    error,
  } = useMoneyHashQuery<Array<RiskRule>>({
    queryKey: ["riskRules", { ...paginationParams, ...router.query }],
    queryFn: ({ signal }) =>
      axiosApiInstance
        .get("/d/risk_management/riskrules/", {
          params: {
            ...paginationParams,
            ...router.query,
          },
          signal,
        })
        .then((res) => res.data),
    keepPreviousData: true,
  });

  const scrollRef = useRestoreScrollOnFetching(isFetching);

  const deleteRuleMutation = useMutation({
    mutationFn: (ruleIdToDelete: string | null) =>
      axiosApiInstance.delete(
        `/d/risk_management/riskrules/${ruleIdToDelete}/`
      ),
    onSuccess: () => {
      toast.success("Rule deleted successfully");
      refetch();
    },
    onError: (error) =>
      toast.error(parseErrorsToArray(error)?.[0] || "Something went wrong!"),
    onSettled: () => setRuleIdToDelete(null),
  });

  const isFiltering = useMemo(
    () =>
      Boolean(
        searchQuery ||
          (listMeta?.filter_enabled &&
            listMeta?.filter_types.some(
              ({ field_name }) => !!router.query[field_name]
            ))
      ),
    [
      searchQuery,
      listMeta?.filter_enabled,
      listMeta?.filter_types,
      router.query,
    ]
  );

  const selectedRule = rules?.find((rule) => rule.id === selectedRowId);

  return (
    <Layout
      ref={scrollRef}
      metaTitle="Risk Management"
      className="flex flex-col gap-4"
    >
      <PageHeader
        title="Manage your risk rules"
        description="Create a rule to trigger risk alert or apply action based on risk level"
        withoutBorder
        actions={
          <AddNewRuleDrawer
            refreshTable={refetch}
            isAddEnabled={listMeta?.add_enabled}
          />
        }
      />

      <RiskNavigationTabs activeTab="riskRules" />

      {isLoading && (
        <span className="pt-6">
          <Loading />
        </span>
      )}

      <AlertModal
        isOpen={!!ruleIdToDelete}
        variant="danger"
        Icon={ExclamationIcon}
        title="Delete rule"
        description="Are you sure you want to delete this rule from the flow?"
        confirmLabel="Delete"
        onConfirm={() => deleteRuleMutation.mutate(ruleIdToDelete)}
        onClose={() => setRuleIdToDelete(null)}
      />
      {isError && <ErrorPage error={error} />}
      {!isError && (
        <>
          {rules?.length && !isLoading ? (
            <>
              {(listMeta?.filter_enabled || listMeta?.search_enabled) && (
                <Filters
                  filters={listMeta.filter_types}
                  enabledFilters={!!listMeta?.filter_enabled}
                  recentStoreKey="risk-rules"
                  search={{
                    enabled: !!listMeta?.search_enabled,
                    placeholder: "Search Risk Rules",
                  }}
                />
              )}

              <div className="flex">
                <div className="grid flex-1 grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {rules?.map((rule) => (
                    <RiskRuleCard
                      key={rule.id}
                      onClick={() => setSelectedRowId(rule.id)}
                      rule={rule}
                      deleteActionName={"Delete rule"}
                      onDeleteRule={(e) => {
                        e.stopPropagation();
                        setRuleIdToDelete(String(rule.id));
                      }}
                    />
                  ))}
                </div>

                <DetailsSheet
                  selectedRowId={selectedRowId}
                  setSelectedRowId={setSelectedRowId}
                  selectedRule={selectedRule}
                  showEditRule={showEditRule}
                  setShowEditRule={setShowEditRule}
                  refreshTable={refetch}
                />
              </div>

              <Pagination
                total={Number(count)}
                onPageChange={({ page, limit }) => {
                  router.replace({
                    query: { ...router.query, page, limit },
                  });
                }}
              />
            </>
          ) : null}

          {!isLoading && rules?.length === 0 && (
            <NoResults
              tableName="Risk rules"
              isFiltering={isFiltering}
              removeAllFilters={() => {
                router.replace({ query: { page: 1 } });
              }}
            />
          )}
        </>
      )}
    </Layout>
  );
}

RiskRulesPage.parentPath = "/risk/rules";

const DetailsSheet = ({
  selectedRowId,
  setSelectedRowId,
  selectedRule,
  setShowEditRule,
  showEditRule,
  refreshTable,
}: {
  selectedRowId: number | null;
  setSelectedRowId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedRule: RiskRule | undefined;
  setShowEditRule: React.Dispatch<React.SetStateAction<boolean>>;
  showEditRule: boolean;
  refreshTable: () => void;
}) => {
  useEffect(() => {
    if (!window.Pylon) return;

    if (!!selectedRowId) window.Pylon("hideChatBubble");

    return () => window.Pylon("showChatBubble");
  }, [selectedRowId]);

  return (
    <Sheet
      open={!!selectedRowId}
      onOpenChange={() => setSelectedRowId(null)}
      noBackdrop
    >
      <Sheet.Popup>
        {!!selectedRowId && (
          <>
            <Sheet.Header>
              <Sheet.SubHeading>Rule #{selectedRule?.id}</Sheet.SubHeading>
              <Sheet.Title>{selectedRule?.name}</Sheet.Title>
            </Sheet.Header>

            <Sheet.Panel className="space-y-6">
              <div>
                <h3 className="mb-2 text-sm font-normal capitalize text-foundations-subtle">
                  Linked Accounts
                </h3>
                {selectedRule?.used_accounts?.length ? (
                  selectedRule?.used_accounts.map((account) => (
                    <div
                      key={account.id}
                      className="mb-2 flex items-center space-x-2 text-sm font-normal text-foundations-subtle"
                    >
                      <p className="inline-flex h-5 w-5 items-center justify-center rounded bg-foundations-bolder text-xs font-medium uppercase text-foundations-inverse ">
                        {account.name.slice(0, 2)}
                      </p>

                      <p>{account.name}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm font-normal text-foundations-subtle">
                    -
                  </p>
                )}
              </div>

              <div>
                <h3 className="mb-2 text-sm font-normal capitalize text-foundations-subtle">
                  Linked Payment Flows
                </h3>
                {selectedRule?.flows?.length ? (
                  selectedRule?.flows.map((flow) => (
                    <div
                      key={flow.id}
                      className="mb-2 flex items-center space-x-2 text-sm font-normal text-foundations-subtle"
                    >
                      <CommandSquareIcon className="h-5 w-5 text-foundations-subtler" />
                      <p>{flow.name}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm font-normal text-foundations-subtle">
                    -
                  </p>
                )}
              </div>

              <div>
                <h3 className="mb-2 text-sm font-normal capitalize text-foundations-subtle">
                  Linked Risk Lists
                </h3>
                {selectedRule?.risk_lists?.length ? (
                  selectedRule?.risk_lists.map((riskList) => (
                    <div
                      key={riskList.id}
                      className="mb-2 flex items-center space-x-2 text-sm font-normal text-foundations-subtle"
                    >
                      <ShieldExclamationIcon className="h-5 w-5 text-foundations-subtler" />
                      <p>{riskList.name}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm font-normal text-foundations-subtle">
                    -
                  </p>
                )}
              </div>
            </Sheet.Panel>

            <Sheet.Footer>
              <IconButton
                variant="secondary"
                className="px-4"
                onClick={() => {
                  setShowEditRule(true);
                }}
              >
                <PencilIcon className="" aria-hidden="true" />
                <span>Edit</span>
              </IconButton>

              {selectedRule && (
                <EditRuleDrawer
                  rule={selectedRule}
                  showEditRule={showEditRule}
                  setShowEditRule={setShowEditRule}
                  refreshTable={refreshTable}
                />
              )}
            </Sheet.Footer>
          </>
        )}
      </Sheet.Popup>
    </Sheet>
  );
};
